import { useEffect, useRef, useState } from "react";
import {
  Button,
  ButtonProps,
  Spinner,
  Tooltip,
  AnchoredOverlay,
  Box,
} from "@primer/react";

interface IMyButtonProps extends ButtonProps {
  text?: string;
  isLoading?: boolean;
  tooltip?: string;
  tooltipdDirection?: "n" | "ne" | "e" | "se" | "s" | "sw" | "w" | "nw";
  isWait?: boolean;

  dropdownContent?: React.ReactNode;
}

const Loading = () => {
  return <Spinner size="small" />;
};

const MyButton = (props: IMyButtonProps) => {
  const [waitDisabled, setWaitDisabled] = useState<boolean>(false);
  const [countdown, setCountdown] = useState<number>(0);
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [side, setSide] = useState<"outside-top" | "outside-bottom">(
    "outside-bottom",
  );

  useEffect(() => {
    if (!buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();

    const spaceBottom = window.innerHeight - rect.bottom;
    const spaceTop = rect.top;

    if (spaceBottom < 300 && spaceTop > spaceBottom) {
      setSide("outside-top");
    } else {
      setSide("outside-bottom");
    }
  }, [open]);

  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;

    if (props.isWait && !props.disabled) {
      setWaitDisabled(true);
      setCountdown(3);

      timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            setWaitDisabled(false);
            if (timer) clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      setWaitDisabled(false);
      setCountdown(0);
    }

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [props.disabled, props.isWait]);

  const disabled = props.isLoading || props.disabled || waitDisabled;

  const displayText =
    countdown > 0 ? `${props.text ?? ""} (${countdown})` : props.text;

  const handleClick = (e: any) => {
    if (props.dropdownContent) {
      setOpen(!open);
    }

    props.onClick?.(e);
  };

  const button = (
    <>
      <Button
        {...props}
        ref={buttonRef}
        size={props.size ?? "small"}
        leadingVisual={props.isLoading ? Loading : props.leadingVisual}
        disabled={disabled}
        onClick={handleClick}
      >
        {displayText}
        {props.children}
      </Button>

      {props.dropdownContent && (
        <AnchoredOverlay
          open={open}
          onClose={() => setOpen(false)}
          anchorRef={buttonRef}
          renderAnchor={(props) => <span {...props} />}
          side={side}
          align="start"
        >
          <Box
            sx={{
              border: "1px solid",
              borderColor: "border.default",
              borderRadius: 2,
              bg: "canvas.default",
              p: 3,
              boxShadow: "shadow.large",
              minWidth: 200,
            }}
          >
            {props.dropdownContent}
          </Box>
        </AnchoredOverlay>
      )}
    </>
  );

  return (
    <>
      {!props.tooltip && button}
      {props.tooltip && (
        <Tooltip
          aria-label={props.tooltip}
          direction={props.tooltipdDirection ?? "n"}
        >
          {button}
        </Tooltip>
      )}
    </>
  );
};

export default MyButton;
